import React from 'react';
import { motion } from 'framer-motion';
import { Users, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toPreTaxPrice, PRICE_EXCLUDES_TAX_LABEL } from '../../utils/pricing';

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800';

const LandingRoomCardGrid = ({ title, subtitle, description, cards, onBook, onWhatsApp }) => {
  if (!Array.isArray(cards) || cards.length === 0) return null;

  return (
    <section className="py-16 sm:py-20 bg-emerald-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          {title && (
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              {title}
            </span>
          )}
          {subtitle && (
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4">
              {subtitle}
            </h2>
          )}
          {description && (
            <p className="text-gray-600 text-base sm:text-lg">{description}</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {cards.map((card, idx) => (
            <motion.div
              key={card.id || idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-soft border border-gray-100 flex flex-col justify-between group hover:shadow-xl transition-shadow"
            >
              <div>
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={card.image || PLACEHOLDER_IMAGE}
                    alt={card.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {card.capacity && (
                    <div className="absolute top-4 left-4 bg-emerald-700/90 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-medium">
                      {card.capacity}
                    </div>
                  )}
                  {card.size && (
                    <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
                      {card.size}
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="font-display text-2xl font-bold text-gray-900 mb-2">{card.name}</h3>
                  {card.bed && (
                    <p className="text-emerald-700 text-xs font-medium mb-3 flex items-center gap-1.5">
                      <Users className="w-4 h-4" /> {card.bed}
                    </p>
                  )}
                  {card.description && (
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {card.description}
                    </p>
                  )}

                  {Array.isArray(card.features) && card.features.length > 0 && (
                    <div className="space-y-2 mb-6">
                      {card.features.map((feat, fIdx) => (
                        <div key={fIdx} className="flex items-center text-xs text-gray-700 gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 pt-0 border-t border-gray-100 mt-auto">
                <div className="flex items-baseline justify-between mb-4">
                  <div>
                    <span className="text-gray-500 text-xs">Mulai dari</span>
                    <div className="text-2xl font-bold text-emerald-700">
                      Rp {toPreTaxPrice(card.price)?.toLocaleString('id-ID')}
                      <span className="text-xs text-gray-500 font-normal"> /malam</span>
                    </div>
                    <span className="text-[10px] text-gray-400">{PRICE_EXCLUDES_TAX_LABEL}</span>
                  </div>
                </div>

                <div className={onWhatsApp ? 'grid grid-cols-2 gap-2' : ''}>
                  <Button
                    onClick={() => onBook && onBook(card.name)}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs sm:text-sm py-2.5 rounded-xl shadow-sm"
                  >
                    Pesan Kamar
                  </Button>
                  {onWhatsApp && (
                    <Button
                      onClick={() => onWhatsApp(`Booking ${card.name}`)}
                      variant="outline"
                      className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs sm:text-sm py-2.5 rounded-xl"
                    >
                      Chat Admin
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LandingRoomCardGrid;